import { LazyStore } from "../../utils/Store";
import { CartContextChangeListener } from "../CartContextChangeListener";
import { CustomerCartStore, CustomerCartStoreImpl, DELIVERY_DATE_FORMAT } from "../customer-cart/CustomerCartStore";
import { DeliveryDatesStore, PickupDatesStore } from "../DatesProvider";
import { DatePickerActions } from "../../actions/DatePickerActions";
import { CheckoutAggregatorStore, CheckoutAggregatorStoreImpl } from "../checkout/CheckoutAggregatorStore";
import { UpdateDeliveryDate, UpdatePickupDate } from "../checkout/DatePickerEvents";
import type { Listenable } from "../../utils/Store";
import type { CheckoutData, Timeslot } from "../../model/CheckoutData";
import type {
	InvalidDeliveryDate,
	InvalidPickupDate,
	ValidDeliveryDate,
	ValidPickupDate,
} from "../../server/StoreInfoRequests";
import { isDateSelectionWithConfiguredTimeslotsEnabled } from "../../utils/featureFlagUtil";
import { parseDate } from "../../utils/DateUtil";

interface IDateHandler extends Listenable<any> {
	getDate: () => Date | null | undefined;
	getTimeslot: () => Timeslot | null | undefined;
	setDate: (date: Date) => Promise<void>;
	setDateAndTimeslot: (date: Date, timeslot: Timeslot | null | undefined) => Promise<void>;
	isDisabled: (hasValidDeliveryDates: boolean) => boolean;
}
type DatePickerStoreState<DateType> = {
	dates: Map<string, DateType>;
	selectedDate: Date | null | undefined;
	selectedTimeslot: Timeslot | null | undefined;
	variant: "editable" | "disabled" | "readonly";
};
type DatesStore<DateType> = CartContextChangeListener<Map<string, DateType>, any>;
export class DatePickerStore<DateType> extends LazyStore {
	_datesStore: DatesStore<DateType>;
	_dateHandler: IDateHandler;
	_state!: {
		selectedDate: Date | null | undefined;
		selectedTimeslot: Timeslot | null | undefined;
	};

	constructor(
		datesStore: DatesStore<DateType>,
		dateHandler: IDateHandler,
		onSelectDate: Listenable<(date: Date, arg1: ValidPickupDate, arg2: Timeslot | null | undefined) => void>,
	) {
		super();
		this._datesStore = datesStore;
		this._dateHandler = dateHandler;
		this.resetState();

		this.onArm = () => {
			this.listenTo(onSelectDate, (date, _, timeslot) => {
				this.setDate(date, timeslot).catch((error) => {
					console.error("setDate in DatePickerStore failed with ", error);
				});
			});
			this.listenTo(this._datesStore, this.trigger);
			this.listenTo(this._dateHandler, this.handleDateChange);
			this.handleDateChange();
		};

		this.onDisarm = this.resetState;
	}

	resetState = () => {
		this._state = {
			selectedDate: null,
			selectedTimeslot: null,
		};
	};
	handleDateChange = () => {
		const selectedDate = this._dateHandler.getDate();

		const selectedTimeslot = this._dateHandler.getTimeslot();

		const { selectedDate: currentDateSelection, selectedTimeslot: currentTimeslot } = this._state;
		const currentAndNewDateNotSelected = !selectedDate && !currentDateSelection;
		const currentAndNewTimeslotNotSelected = !selectedTimeslot && !currentTimeslot;
		const dateUnchanged =
			currentAndNewDateNotSelected ||
			(selectedDate && currentDateSelection && currentDateSelection.getTime() === selectedDate.getTime());
		const timeslotUnchanged =
			currentAndNewTimeslotNotSelected ||
			(selectedTimeslot &&
				currentTimeslot &&
				currentTimeslot.fromUtc === selectedTimeslot.fromUtc &&
				currentTimeslot.untilUtc === selectedTimeslot.untilUtc);

		if (dateUnchanged && timeslotUnchanged) {
			return;
		}

		this._state = {
			selectedDate,
			selectedTimeslot,
		};
		this.trigger();
	};
	setDate = async (date: Date, timeslot: Timeslot | null | undefined) => {
		await this._dateHandler.setDateAndTimeslot(date, timeslot);
	};
	getState = (): DatePickerStoreState<Date> => {
		const dates: Map<string, Date> = this._datesStore.getState() || new Map();
		const hasValidDeliveryDates = Array.from(dates).some(
			(
				[, dateInfo], // @ts-expect-error
			) => dateInfo.type === "ValidDate" && dateInfo.deliveryTimes && dateInfo.deliveryTimes.length,
		);
		const isDisabled = !dates.size || this._dateHandler.isDisabled(hasValidDeliveryDates);
		return {
			dates: dates,
			selectedDate: this._state.selectedDate,
			selectedTimeslot: this._state.selectedTimeslot,
			variant: isDisabled ? "disabled" : "editable",
		};
	};
}
export class CheckoutDeliveryDateHandler extends LazyStore implements IDateHandler {
	_checkoutStore: CheckoutAggregatorStoreImpl;

	constructor(checkoutStore: CheckoutAggregatorStoreImpl) {
		super();
		this._checkoutStore = checkoutStore;

		this.onArm = () => {
			this.listenTo(this._checkoutStore, this.trigger);
		};
	}

	setDateAndTimeslot = async (date: Date) => {
		await this.setDate(date);
	};
	getTimeslot = () => null;
	getDate = () => {
		const checkoutData = this._checkoutStore.getCheckoutData();
		return parseDate(checkoutData?.header.preferredDeliveryDate);
	};
	setDate = async (date: Date) => {
		this._checkoutStore.queueAction(new UpdateDeliveryDate(date));
	};
	isDisabled = (hasValidDeliveryDates: boolean) => {
		const checkoutData = this._checkoutStore.getCheckoutData();

		const isEmployee = !!checkoutData?.context.employeeId;
		return !checkoutData || !isCustomDateSupported(checkoutData, hasValidDeliveryDates, isEmployee);
	};
}

function isCustomDateSupported(
	checkoutData: CheckoutData,
	hasValidDeliveryDates: boolean,
	isEmployee: boolean,
): boolean {
	return (
		isEmployee ||
		!isDateSelectionWithConfiguredTimeslotsEnabled() ||
		(isDateSelectionWithConfiguredTimeslotsEnabled() && containsFsd(checkoutData) && hasValidDeliveryDates)
	);
}

function containsFsd(checkoutData: CheckoutData): boolean {
	return Object.keys(checkoutData.groupHeaders)
		.map((headerKey) => checkoutData.groupHeaders[headerKey])
		.some((header) => header && header.fulfillmentType === "FSD");
}

export class CartDeliveryDateHandler extends LazyStore implements IDateHandler {
	_cartStore: CustomerCartStoreImpl;

	constructor(cartStore: CustomerCartStoreImpl) {
		super();
		this._cartStore = cartStore;

		this.onArm = () => {
			this.listenTo(this._cartStore, this.trigger);
		};
	}

	setDateAndTimeslot = async (date: Date) => {
		await this.setDate(date);
	};
	getTimeslot = () => null;
	getDate = () => {
		const selectedDateString = this._cartStore.getDeliveryDate();
		const length = selectedDateString?.length ?? 0;
		return parseDate(selectedDateString, length > 10 ? DELIVERY_DATE_FORMAT : undefined);
	};
	setDate = async (date: Date) => {
		await this._cartStore.setDeliveryDate(date);
	};
	isDisabled = () => !this._cartStore.getCart();
}
export const CheckoutDeliveryDatePickerStore = new DatePickerStore<ValidDeliveryDate | InvalidDeliveryDate>(
	DeliveryDatesStore,
	new CheckoutDeliveryDateHandler(CheckoutAggregatorStore),
	DatePickerActions.setCheckoutDeliveryDate,
);
export class PickupDateHandler extends LazyStore implements IDateHandler {
	_checkoutStore: CheckoutAggregatorStoreImpl;

	constructor(checkoutStore: CheckoutAggregatorStoreImpl) {
		super();
		this._checkoutStore = checkoutStore;

		this.onArm = () => {
			this.listenTo(this._checkoutStore, this.trigger);
		};
	}

	setDateAndTimeslot = async (date: Date, timeslot: Timeslot | null | undefined) => {
		this._checkoutStore.queueAction(new UpdatePickupDate(date, timeslot));
	};
	getTimeslot = () => {
		const checkoutData = this._checkoutStore.getCheckoutData();
		return checkoutData?.header.preferredPickupDate?.timeslot ?? null;
	};
	getDate = () => {
		const checkoutData = this._checkoutStore.getCheckoutData();
		return parseDate(checkoutData?.header.preferredPickupDate?.date);
	};
	setDate = async (date: Date) => {
		this._checkoutStore.queueAction(new UpdatePickupDate(date));
	};
	isDisabled = () => !this._checkoutStore.getCheckoutData();
}
export const CartDeliveryDatePickerStore = new DatePickerStore<ValidDeliveryDate | InvalidDeliveryDate>(
	DeliveryDatesStore,
	new CartDeliveryDateHandler(CustomerCartStore),
	DatePickerActions.setCartDeliveryDate,
);
export const PickupDatePickerStore = new DatePickerStore<ValidPickupDate | InvalidPickupDate>(
	PickupDatesStore,
	new PickupDateHandler(CheckoutAggregatorStore),
	DatePickerActions.setPickupDate,
);
